/* stylelint-disable no-descending-specificity */

import ScrollArrow from '@/components/shared/ScrollArrow';
import type { Milliseconds } from '@lemonade-hq/ts-helpers';
import React from 'react';
import styled, { type Keyframes, keyframes, css } from 'styled-components';

// TODO: move to monolu, add callbacks for timings maybe?
const STORY_PADDING = 10;
const STROKE_WIDTH = '0.3em';
const STORY_SIZE = 100; // so it matches 100%
const STORY_LENGTH = STORY_SIZE - STORY_PADDING * 2;
const STORY_GAP = 6;
const STORY_PATH = `M ${STORY_PADDING} ${STORY_PADDING}
L ${STORY_SIZE - STORY_PADDING} ${STORY_PADDING}`;
const STORY_WIDTH = '10em';

const storyBarAnimation = ({
  sections,
  duration,
  delay,
}: {
  sections: number;
  duration: number;
  delay: number;
}): Keyframes => {
  const new100 = 100 * ((duration * sections) / (duration * sections + delay));
  return keyframes`
        0% {
            opacity: 1;
            width: ${STORY_PADDING - 1}%;
        }

        ${new100 * 0.95}% {
            opacity: 1;
        }

        ${new100}% {
            width: ${STORY_SIZE - STORY_PADDING + 5}%;
        }

        100% {
            opacity: 1;
            width: 0;
        }
    `;
};

const arrowAnimation = ({
  sections,
  duration,
  delay,
}: {
  sections: number;
  duration: number;
  delay: number;
}): Keyframes => {
  const new100 = 100 * ((duration * sections) / (duration * sections + delay));
  return keyframes`
        0% {
            opacity: 0;
        }

        ${new100 * 0.975}% {
            opacity: 0;
        }

        ${new100}% {
            opacity: 1
        }

        95% {
            opacity: 1;
        }

        100% {
            opacity: 0;
        }
    `;
};

const StoryTimelineProgressContainer = styled.div<{
  sections: number;
  duration: number;
  delay: number;
}>`
  ${({ sections, duration, delay }) => css`
    animation-name: ${storyBarAnimation({ sections, duration, delay })};
    animation-duration: ${duration * sections + delay}ms;
  `};
  animation-timing-function: linear;
  animation-fill-mode: both;
  animation-iteration-count: infinite;
  overflow: hidden;

  svg {
    width: ${STORY_WIDTH};
    stroke-width: calc(${STROKE_WIDTH} * 0.1);
  }
`;

const StoryTimelineSVG = styled.svg.attrs({
  viewBox: `0 0 ${STORY_SIZE} ${STORY_SIZE}`,
})<{ sections: number }>`
  fill: none;

  path {
    stroke-width: ${STROKE_WIDTH};
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: ${({ sections }) => `${(STORY_LENGTH - (sections - 1) * STORY_GAP) / sections} ${STORY_GAP}`};
  }
`;

const StoryTimelineContainer = styled.div`
  height: ${STROKE_WIDTH};

  > *:first-child {
    position: absolute;
    top: 0;
    left: 0;
  }
`;

const ScrollArrowContainer = styled.div<{
  sections: number;
  duration: number;
  delay: number;
}>`
  margin: 9em auto 0 auto;
  ${({ sections, duration, delay }) => css`
    animation-name: ${arrowAnimation({ sections, duration, delay })};
    animation-duration: ${duration * sections + delay}ms;
  `};
  animation-timing-function: linear;
  animation-fill-mode: both;
  animation-iteration-count: infinite;
`;

const StyledScrollArrow = styled(ScrollArrow)`
  --width: 3.5em;
  width: var(--width) !important;
  margin: 0 auto;
`;

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  presentationMode: boolean;
  sections: number;
  paused: boolean;
  durationPerSection: Milliseconds;
  delayBeforeRestart?: Milliseconds;
  progressColor: string;
  foregroundColor: string;
}

const StoryTimeline: React.FC<Props> = ({
  presentationMode,
  progressColor,
  foregroundColor,
  sections,
  paused,
  durationPerSection,
  delayBeforeRestart = 0,
  ...props
}) => {
  return (
    <StoryTimelineContainer {...props}>
      <StoryTimelineSVG sections={sections}>
        <path d={STORY_PATH} style={{ stroke: `${foregroundColor} !important` }} />
      </StoryTimelineSVG>
      <StoryTimelineProgressContainer
        sections={sections}
        duration={durationPerSection}
        delay={delayBeforeRestart}
        style={{
          animationPlayState: paused ? 'paused' : 'running',
        }}
      >
        <StoryTimelineSVG sections={sections}>
          <path d={STORY_PATH} style={{ stroke: `${progressColor} !important` }} />
        </StoryTimelineSVG>
      </StoryTimelineProgressContainer>
      {!presentationMode && (
        <ScrollArrowContainer
          style={{ animationPlayState: paused ? 'paused' : 'running' }}
          sections={sections}
          duration={durationPerSection}
          delay={delayBeforeRestart}
        >
          <StyledScrollArrow style={{ animationPlayState: paused ? 'paused' : 'running' }} />
        </ScrollArrowContainer>
      )}
    </StoryTimelineContainer>
  );
};

export default React.memo(StoryTimeline);
